import { useState, useEffect, useMemo } from 'react';
import { SEO } from '@/components/SEO';
import { QueryErrorBoundary } from '@/components/QueryErrorBoundary';
import { Header } from '@/components/Header';
import { BottomNav } from '@/components/BottomNav';
import { QuickNavMenu } from '@/components/QuickNavMenu';
import { PlayerCard } from '@/components/PlayerCard';
import { ClubCard } from '@/components/ClubCard';
import { RankBadge } from '@/components/RankBadge';
import { SportFilter } from '@/components/SportFilter';
import { UrgentMatchesSection } from '@/components/UrgentMatchesSection';
import { NearbyMatchCard } from '@/components/NearbyMatchCard';
import { MiniClassCard } from '@/components/MiniClassCard';
import { MiniCourtScheduleCard } from '@/components/MiniCourtScheduleCard';
import { UpcomingSessionsSection } from '@/components/UpcomingSessionsSection';
import { ClubActivitiesSection } from '@/components/ClubActivitiesSection';
import { PersonalizedHeroSection } from '@/components/PersonalizedHeroSection';
import { FriendsOnlineWidget } from '@/components/FriendsOnlineWidget';
import { Button } from '@/components/ui/button';
import { 
  ChevronRight, Trophy, Flame, Navigation, Plus, 
  Swords, Loader2, MapPin, SlidersHorizontal, GraduationCap, CalendarDays 
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Sport, Club } from '@/types';
import { usePlayers } from '@/hooks/usePlayers';
import { useClubs } from '@/hooks/useClubs';
import { useNearbyMatches, useUrgentMatches } from '@/hooks/useNearbyMatches';
import { useUserFollows, useClubFollows } from '@/hooks/useFollows';
import { useAuth } from '@/hooks/useAuth';
import { useGeolocation } from '@/hooks/useGeolocation';
import { useTrainingClasses } from '@/hooks/useTrainingClasses';
import { useCourtSchedules } from '@/hooks/useCourtSchedules';
import { useHomepagePersonalization } from '@/hooks/useHomepagePersonalization';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@/components/ui/sheet';
import { Slider } from '@/components/ui/slider';
import { Label } from '@/components/ui/label';

export default function Index() {
  const navigate = useNavigate();
  const { profile } = useAuth();
  
  // Personalization hook
  const personalization = useHomepagePersonalization();
  
  // Use personalized default sport filter
  const [sportFilter, setSportFilter] = useState<Sport | 'all'>('all');
  const [radiusKm, setRadiusKm] = useState(5);
  const [showNearbyList, setShowNearbyList] = useState(false);
  
  // Set default sport filter based on personalization
  useEffect(() => {
    if (personalization.defaultSportFilter !== 'all' && sportFilter === 'all') {
      setSportFilter(personalization.defaultSportFilter);
    }
  }, [personalization.defaultSportFilter]);
  
  const { 
    latitude, 
    longitude, 
    loading: isLocating, 
    requestLocation, 
    locationName,
    hasLocation 
  } = useGeolocation();

  const { data: allPlayers = [], isError: playersError, refetch: refetchPlayers } = usePlayers();
  const { data: allClubs = [], isError: clubsError, refetch: refetchClubs } = useClubs();
  const { followingUsers } = useUserFollows();
  const { followingClubs } = useClubFollows();
  
  // Training classes and court schedules for homepage sections
  const { data: trainingClasses = [] } = useTrainingClasses({
    sport: sportFilter === 'all' ? undefined : sportFilter,
  });
  const { data: courtSchedules = [] } = useCourtSchedules({
    sportFilter: sportFilter,
  });

  // Nearby matches with location filter
  const { 
    data: nearbyMatches = [], 
    isLoading: isLoadingNearby 
  } = useNearbyMatches({
    latitude,
    longitude,
    radiusKm,
    sport: sportFilter,
    maxHours: 24,
    enabled: true,
  });

  // Urgent matches (starting within 2 hours)
  const { 
    data: urgentMatches = [], 
    isLoading: isLoadingUrgent 
  } = useUrgentMatches({
    latitude,
    longitude,
    sport: sportFilter,
    enabled: true,
  });

  const handleFindNearby = () => {
    if (!hasLocation) {
      requestLocation();
    }
    setShowNearbyList(true);
  };

  // Auto-show nearby list when location is obtained
  useEffect(() => {
    if (hasLocation && !showNearbyList) {
      setShowNearbyList(true);
    }
  }, [hasLocation]);

  // Sort and prioritize matches using personalization
  const sortedNearbyMatches = useMemo(() => {
    return [...nearbyMatches]
      .map(match => ({
        ...match,
        priorityScore: personalization.calculateMatchPriority(match),
      }))
      .sort((a, b) => b.priorityScore - a.priorityScore);
  }, [nearbyMatches, personalization.calculateMatchPriority]);

  // Sort and prioritize classes using personalization
  const sortedClasses = useMemo(() => {
    return [...trainingClasses]
      .map(cls => ({
        ...cls,
        priorityScore: personalization.calculateClassPriority(cls),
      }))
      .sort((a, b) => b.priorityScore - a.priorityScore);
  }, [trainingClasses, personalization.calculateClassPriority]);

  // Filter players by sport and sort by following + rank
  const sortedPlayers = useMemo(() => {
    return [...allPlayers]
      .filter(p => sportFilter === 'all' || p.sports?.includes(sportFilter))
      .sort((a, b) => {
        const aFollowed = followingUsers.includes(a.id);
        const bFollowed = followingUsers.includes(b.id);
        if (aFollowed && !bFollowed) return -1;
        if (!aFollowed && bFollowed) return 1;
        return b.rank_points - a.rank_points;
      });
  }, [allPlayers, sportFilter, followingUsers]);

  // Filter clubs by sport and sort by following + rating
  const sortedClubs = useMemo(() => {
    return [...allClubs]
      .filter(c => sportFilter === 'all' || c.sport === sportFilter)
      .sort((a, b) => {
        const aFollowed = followingClubs.includes(a.id);
        const bFollowed = followingClubs.includes(b.id);
        if (aFollowed && !bFollowed) return -1;
        if (!aFollowed && bFollowed) return 1;
        return Number(b.rating) - Number(a.rating);
      });
  }, [allClubs, sportFilter, followingClubs]);

  const mapToClubType = (club: typeof allClubs[0]): Club => ({
    id: club.id,
    name: club.name,
    avatar: club.avatar || 'https://images.unsplash.com/photo-1574629810360-7efbbe195018?w=200&h=200&fit=crop',
    sport: club.sport as Sport,
    location: club.location || '',
    memberCount: club.member_count,
    rating: Number(club.rating),
    reviewCount: club.review_count,
    isRecruiting: club.is_recruiting,
    isLookingForMatch: club.is_looking_for_match,
    tags: club.tags || [],
    description: club.description || ''
  });

  // Get section limits from personalization
  const getLimit = (section: string) => {
    return personalization.sectionConfigs[section as keyof typeof personalization.sectionConfigs]?.limit ?? 3;
  };

  // Check section visibility
  const isSectionVisible = (section: string) => {
    return personalization.sectionConfigs[section as keyof typeof personalization.sectionConfigs]?.visible ?? true;
  };

  return (
    <div className="min-h-screen bg-background pb-24">
      <SEO
        title="SportRank — Kết nối thể thao & Xếp hạng người chơi"
        description="Tìm trận đấu, CLB, sân bãi và lớp học thể thao gần bạn. Theo dõi điểm rank, kết bạn và thanh toán an toàn trên SportRank."
        pathname="/"
      />
      <Header />
      <QuickNavMenu />

      {(playersError || clubsError) && (
        <div className="px-4 pt-4">
          <QueryErrorBoundary 
            error={new Error('Failed to load data')} 
            refetch={() => { refetchPlayers(); refetchClubs(); }}
            compact
          />
        </div>
      )}

      <main className="px-4 py-4 space-y-6">
        {/* Personalized Hero Section */}
        <PersonalizedHeroSection
          userName={profile?.name?.split(' ')[0]}
          userSegment={personalization.userSegment}
          primaryCTA={personalization.primaryCTA}
          ctaMessage={personalization.ctaMessage}
          hasLocation={hasLocation}
          locationName={locationName}
          isLocating={isLocating}
          onFindNearby={handleFindNearby}
          upcomingSessionCount={personalization.hasUpcomingSessions ? getLimit('upcomingSessions') : 0}
          activeEnrollmentCount={personalization.hasActiveEnrollments ? 1 : 0}
        />

        {/* Sports Quick Filter - always visible */}
        <section>
          <SportFilter value={sportFilter} onChange={setSportFilter} />
        </section>

        {/* Dynamic Section: Upcoming Sessions (for training students) */}
        {isSectionVisible('upcomingSessions') && personalization.hasUpcomingSessions && (
          <UpcomingSessionsSection limit={getLimit('upcomingSessions')} />
        )}

        {/* Friends Online Widget */}
        {profile && <FriendsOnlineWidget />}

        {/* Dynamic Section: Club Activities (for club members) */}
        {isSectionVisible('clubActivities') && personalization.userSegment === 'club_member' && (
          <ClubActivitiesSection limit={getLimit('clubActivities')} />
        )}

        {/* Urgent Matches Section - Only show if there are urgent matches */}
        {isSectionVisible('urgentMatches') && (
          <UrgentMatchesSection 
            matches={urgentMatches} 
            isLoading={isLoadingUrgent}
          />
        )}

        {/* Nearby Matches Section */}
        {isSectionVisible('nearbyMatches') && (
          <section>
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-primary/20">
                  <Navigation className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <h3 className="font-bold text-foreground">Trận gần bạn</h3>
                  {hasLocation && (
                    <p className="text-xs text-muted-foreground">
                      Trong bán kính {radiusKm}km
                    </p>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-2">
                {/* Filter Sheet */}
                <Sheet>
                  <SheetTrigger asChild>
                    <Button variant="ghost" size="sm" className="h-8 px-2">
                      <SlidersHorizontal className="w-4 h-4" />
                    </Button>
                  </SheetTrigger>
                  <SheetContent side="bottom" className="h-auto">
                    <SheetHeader>
                      <SheetTitle>Bộ lọc tìm kiếm</SheetTitle>
                    </SheetHeader>
                    <div className="py-6 space-y-6">
                      <div className="space-y-3">
                        <Label>Khoảng cách: {radiusKm}km</Label>
                        <Slider
                          value={[radiusKm]}
                          onValueChange={([value]) => setRadiusKm(value)}
                          min={1}
                          max={20}
                          step={1}
                          className="w-full"
                        />
                        <div className="flex justify-between text-xs text-muted-foreground">
                          <span>1km</span>
                          <span>20km</span>
                        </div>
                      </div>
                    </div>
                  </SheetContent>
                </Sheet>

                <Button 
                  variant="ghost" 
                  size="sm" 
                  className="text-muted-foreground hover:text-primary h-8 px-2"
                  onClick={() => navigate('/matches')}
                >
                  Tất cả
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            </div>

            {/* Nearby matches list - using prioritized sorting */}
            <div className="space-y-3">
              {isLoadingNearby ? (
                <div className="space-y-3">
                  {[1, 2, 3].map((i) => (
                    <div key={i} className="animate-pulse bg-muted/50 rounded-xl h-48" />
                  ))}
                </div>
              ) : sortedNearbyMatches.length > 0 ? (
                sortedNearbyMatches.slice(0, getLimit('nearbyMatches')).map((match) => (
                  <NearbyMatchCard key={match.id} match={match} />
                ))
              ) : (
                <div className="text-center py-8 bg-muted/30 rounded-xl border border-dashed border-border">
                  <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-primary/10 flex items-center justify-center">
                    <Swords className="w-8 h-8 text-primary" />
                  </div>
                  {!hasLocation ? (
                    <>
                      <p className="text-foreground font-medium mb-1">
                        Bật vị trí để tìm trận gần bạn
                      </p>
                      <p className="text-muted-foreground text-sm mb-4">
                        Cho phép truy cập vị trí để xem các trận đấu đang tuyển người trong khu vực của bạn
                      </p>
                      <Button 
                        size="sm" 
                        className="bg-gradient-to-r from-primary to-accent"
                        onClick={handleFindNearby}
                        disabled={isLocating}
                      >
                        {isLocating ? (
                          <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                        ) : (
                          <MapPin className="w-4 h-4 mr-1" />
                        )}
                        Bật định vị
                      </Button>
                    </>
                  ) : (
                    <>
                      <p className="text-foreground font-medium mb-1">
                        Chưa có trận đấu gần bạn
                      </p>
                      <p className="text-muted-foreground text-sm mb-4">
                        Hiện không có trận nào trong bán kính {radiusKm}km đang tuyển thêm người chơi
                      </p>
                      <div className="flex gap-2 justify-center">
                        <Button 
                          size="sm" 
                          variant="outline"
                          onClick={() => navigate('/matches')}
                        >
                          Xem tất cả trận
                        </Button>
                        <Button 
                          size="sm" 
                          className="bg-gradient-to-r from-primary to-accent"
                          onClick={() => navigate('/matches/create')}
                        >
                          <Plus className="w-4 h-4 mr-1" />
                          Tạo trận mới
                        </Button>
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>
          </section>
        )}

        {/* Featured Classes - with prioritized sorting */}
        {isSectionVisible('classes') && sortedClasses.length > 0 && (
          <section>
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <GraduationCap className="w-5 h-5 text-accent" />
                <h3 className="font-bold text-foreground">Lớp học nổi bật</h3>
              </div>
              <Button 
                variant="ghost" 
                size="sm" 
                className="text-muted-foreground hover:text-accent h-8 px-2"
                onClick={() => navigate('/classes')}
              >
                Xem thêm
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
            <div className="space-y-2">
              {sortedClasses.slice(0, getLimit('classes')).map(cls => (
                <MiniClassCard 
                  key={cls.id} 
                  trainingClass={cls}
                  onClick={() => navigate(`/classes/${cls.id}`)}
                />
              ))}
            </div>
          </section>
        )}

        {/* Available Courts */}
        {isSectionVisible('courts') && courtSchedules.filter(s => s.is_available).length > 0 && (
          <section>
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <CalendarDays className="w-5 h-5 text-primary" />
                <h3 className="font-bold text-foreground">Sân trống</h3>
              </div>
              <Button 
                variant="ghost" 
                size="sm" 
                className="text-muted-foreground hover:text-primary h-8 px-2"
                onClick={() => navigate('/courts')}
              >
                Xem thêm
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
            <div className="space-y-2">
              {courtSchedules
                .filter(s => s.is_available)
                .slice(0, getLimit('courts'))
                .map(schedule => (
                  <MiniCourtScheduleCard key={schedule.id} schedule={schedule} userLatitude={latitude} userLongitude={longitude} />
                ))}
            </div>
          </section>
        )}

        {/* Top Players */}
        {isSectionVisible('players') && (
          <section>
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <Flame className="w-5 h-5 text-accent" />
                <h3 className="font-bold text-foreground">Người chơi nổi bật</h3>
              </div>
              <Button 
                variant="ghost" 
                size="sm" 
                className="text-muted-foreground hover:text-accent h-8 px-2" 
                onClick={() => navigate('/leaderboard')}
              >
                Xem thêm
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
            <div className="space-y-2">
              {sortedPlayers.slice(0, getLimit('players')).map(player => (
                <PlayerCard 
                  key={player.id} 
                  player={player} 
                  onClick={() => navigate(`/players/${player.id}`)} 
                />
              ))}
            </div>
          </section>
        )}

        {/* Popular Clubs */}
        {isSectionVisible('clubs') && (
          <section>
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <Trophy className="w-5 h-5 text-accent" />
                <h3 className="font-bold text-foreground">CLB nổi bật</h3>
              </div>
              <Button 
                variant="ghost" 
                size="sm" 
                className="text-muted-foreground hover:text-accent h-8 px-2" 
                onClick={() => navigate('/clubs')}
              >
                Xem thêm
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
            <div className="space-y-2">
              {sortedClubs.slice(0, getLimit('clubs')).map(club => (
                <ClubCard 
                  key={club.id} 
                  club={mapToClubType(club)} 
                  onClick={() => navigate(`/clubs/${club.id}`)} 
                />
              ))}
            </div>
          </section>
        )}

        {/* Rank Showcase - show for new users */}
        {isSectionVisible('rankShowcase') && personalization.isNewUser && (
          <section className="match-tile p-6">
            <h3 className="text-lg font-bold text-foreground mb-4 text-center">Hệ thống Rank</h3>
            <div className="flex flex-wrap justify-center gap-2">
              {(['bronze', 'silver', 'gold', 'platinum', 'diamond', 'master'] as const).map(rank => (
                <RankBadge key={rank} rank={rank} size="md" />
              ))}
            </div>
            <p className="text-muted-foreground text-sm text-center mt-4">
              Rank được tính tự động dựa trên kết quả thi đấu
            </p>
          </section>
        )}
      </main>

      <BottomNav />
    </div>
  );
}
